/**
 * PDF Take-off Pin Service - Links structure take-offs to PDF locations
 */

import { db } from './firebase';
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  deleteDoc,
  updateDoc,
  doc,
  Timestamp,
} from 'firebase/firestore';
import type { PdfTakeoffLink, PdfTakeoffPin } from '../types';

const COLLECTION = 'pdfTakeoffPins';

function fromFirestore(docSnap: { id: string; data: () => Record<string, unknown> }): PdfTakeoffPin {
  const d = docSnap.data();
  return {
    id: docSnap.id,
    documentId: d.documentId as string,
    documentUrl: d.documentUrl as string,
    leadId: d.leadId as string,
    dealId: d.dealId as string,
    structureId: d.structureId as string,
    structureName: d.structureName as string,
    pageNumber: d.pageNumber as number,
    x: d.x as number,
    y: d.y as number,
    organizational: d.organizational === true,
    createdAt: (d.createdAt as { toDate: () => Date })?.toDate?.() ?? new Date(d.createdAt as string),
  };
}

/** Create a take-off pin */
export async function createTakeoffPin(pin: Omit<PdfTakeoffPin, 'id'>): Promise<string> {
  const ref = await addDoc(collection(db, COLLECTION), {
    documentId: pin.documentId,
    documentUrl: pin.documentUrl,
    leadId: pin.leadId,
    dealId: pin.dealId,
    structureId: pin.structureId,
    structureName: pin.structureName,
    pageNumber: pin.pageNumber,
    x: pin.x,
    y: pin.y,
    ...(pin.organizational ? { organizational: true } : {}),
    createdAt: Timestamp.fromDate(pin.createdAt),
  });
  return ref.id;
}

/** One-shot fetch of all take-off pins for a deal. */
export async function fetchDealTakeoffPins(dealId: string): Promise<PdfTakeoffPin[]> {
  const q = query(collection(db, COLLECTION), where('dealId', '==', dealId));
  const snapshot = await getDocs(q);
  return snapshot.docs
    .map(d => fromFirestore({ id: d.id, data: () => d.data() }))
    .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
}

/** Get take-off pins for a document */
export async function getTakeoffPinsForDocument(
  documentId: string,
  documentUrl: string
): Promise<PdfTakeoffPin[]> {
  const q = query(collection(db, COLLECTION), where('documentId', '==', documentId));
  const snapshot = await getDocs(q);
  let pins = snapshot.docs.map(d => fromFirestore({ id: d.id, data: () => d.data() }));

  if (pins.length === 0 && documentUrl) {
    const q2 = query(collection(db, COLLECTION), where('documentUrl', '==', documentUrl));
    const snap2 = await getDocs(q2);
    pins = snap2.docs.map(d => fromFirestore({ id: d.id, data: () => d.data() }));
  }

  return pins.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
}

/** Delete take-off pins with no linked items on their page (keeps organizational labels). */
export async function pruneOrphanedStructurePins(
  pins: PdfTakeoffPin[],
  links: Array<Pick<PdfTakeoffLink, 'pageNumber' | 'structureId'>>,
): Promise<number> {
  const orphaned = pins.filter(pin => {
    if (pin.organizational) return false;
    if (!pin.id) return false;
    return !links.some(
      link => link.pageNumber === pin.pageNumber && link.structureId === pin.structureId,
    );
  });
  if (orphaned.length === 0) return 0;
  await Promise.all(orphaned.map(pin => deleteTakeoffPin(pin.id!)));
  return orphaned.length;
}

/** Delete a take-off pin */
export async function deleteTakeoffPin(pinId: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTION, pinId));
}

/** Update pin position (normalized 0–100 %). */
export async function updateTakeoffPin(
  pinId: string,
  updates: Partial<Pick<PdfTakeoffPin, 'x' | 'y' | 'pageNumber'>>,
): Promise<void> {
  await updateDoc(doc(db, COLLECTION, pinId), updates);
}
